import React, { useEffect, useState, useContext, useMemo } from "react";
import Navbar from "../../layout/Navbar/Navbar";
import Sidebar from "../../layout/Sidebar/SideBar.jsx";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import blogFetch from "../../axios/config";
import {
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ModalAddUser from "./RegisterUser";
import UserGroupsModal from "./UserGroupsModal";
import { IUser } from "./UserGroupsInterfaces";
import Context from "../../context/Context";
import language from "../../nls/language.json";

export default function UserPermission() {
  const [users, setUsers] = useState<IUser[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | undefined>(
    undefined
  );
  const [search, setSearch] = useState("");

  const {
    getStatusSideBar,
    selectedCountry
  } = useContext(Context);

  const lang = (language as Record<string, any>)[selectedCountry.name].Permissoes;

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    try {
      let response = await blogFetch.get("/user?take=1000");
      let data = response.data;
      setUsers(data);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const term = search.toLowerCase().trim();
    return users.filter((u) =>
      [
        u.user_UserName,
        u.user_Matricula,
        u.user_SAPMatricula,
        u.user_Email,
        u.sector?.stR_description,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [users, search]);

  const handleRowClick = (user: IUser) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className={`app_body ${getStatusSideBar()}`}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <TextField
            size="small"
            placeholder="Pesquisar por nome, matrícula, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 420,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#f8f9fa",
                fontSize: 13,
                "&:hover fieldset": { borderColor: "#9ed6de" },
                "&.Mui-focused fieldset": { borderColor: "#9ed6de" },
              },
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Typography
              variant="body2"
              sx={{ color: "#888", fontSize: 12, whiteSpace: "nowrap" }}
            >
              {filteredUsers.length} de {users.length} usuários
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowModalAdd(true)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: 13,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {lang.usuario.btnAdicionar}
            </Button>
          </div>
        </div>

        <TableContainer
          sx={{
            borderRadius: "8px",
            border: "1px solid #e8e8e8",
            maxHeight: "calc(100vh - 210px)",
            overflow: "auto",
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {[
                  lang.usuario.tableHeader.nome,
                  lang.usuario.tableHeader.matricula,
                  lang.usuario.tableHeader.SAPMatricula,
                  lang.usuario.tableHeader.email,
                  lang.usuario.tableHeader.setor,
                  lang.usuario.tableHeader.editar,
                ].map((header, i) => (
                  <TableCell
                    key={i}
                    align={i === 5 ? "center" : "left"}
                    sx={{
                      backgroundColor: "#3d3d3d",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 12,
                      padding: "10px 14px",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      textAlign: "center",
                      padding: "32px 14px",
                      color: "#999",
                      fontSize: 13,
                    }}
                  >
                    {search.trim()
                      ? `Nenhum usuário encontrado para "${search}"`
                      : "Nenhum usuário cadastrado"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user, index) => (
                  <TableRow
                    key={user.user_Id}
                    onClick={() => handleRowClick(user)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                      transition: "background-color 0.15s",
                      "&:hover": { backgroundColor: "#e8f6f8" },
                      "& td": {
                        fontSize: 13,
                        padding: "9px 14px",
                        borderBottom: "1px solid #eee",
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {user.user_UserName}
                    </TableCell>
                    <TableCell>{user.user_Matricula}</TableCell>
                    <TableCell>{user.user_SAPMatricula}</TableCell>
                    <TableCell>{user.user_Email}</TableCell>
                    <TableCell>
                      {user.sector ? user.sector.stR_description : ""}
                    </TableCell>
                    <TableCell align="center">
                      <ModeEditIcon
                        sx={{ fontSize: 18, color: "#757575" }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <UserGroupsModal
        open={showModal}
        onClose={() => setShowModal(false)}
        user={selectedUser}
      />
      <ModalAddUser
        open={showModalAdd}
        onClose={() => setShowModalAdd(false)}
        getUser={getUsers}
      />
    </>
  );
}
